import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Rocket, Coins, ShieldCheck, Mail, ArrowRight, CheckCircle2, TrendingUp, Users, Globe } from 'lucide-react';

const emptyForm = {
  token_symbol: '', token_name: '', category: 'trade',
  logo_url: '', description: '',
  website: '', whitepaper_url: '', twitter: '', telegram: '', discord: '', github: '',
  total_supply: '', circulating_supply: '', initial_price_usd: '',
  reason: '', contact_email: '',
};

export default function ListYourCoin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    api.get('/listings/info').then(r => setInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user?.email && !form.contact_email) {
      setForm(f => ({ ...f, contact_email: user.email }));
    }
  }, [user]); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!user) {
      toast.info('Please log in or sign up to submit an application');
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_supply: form.total_supply ? parseFloat(form.total_supply) : null,
        circulating_supply: form.circulating_supply ? parseFloat(form.circulating_supply) : null,
        initial_price_usd: form.initial_price_usd ? parseFloat(form.initial_price_usd) : null,
      };
      const r = await api.post('/listings/apply', payload);
      setSubmitted(r.data);
      toast.success(`Application submitted — ID: ${r.data.application_id}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (info && info.enabled === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center">
        <div className="text-[#FCD535] uppercase tracking-widest text-xs mb-3">Applications closed</div>
        <h1 className="font-display text-white text-4xl font-bold tracking-tight mb-3">Listing applications are temporarily paused</h1>
        <p className="text-[#848E9C]">Please check back soon. In the meantime, reach out to <a href={`mailto:${info.contact_email}`} className="text-[#FCD535] hover:underline">{info.contact_email}</a>.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="list-your-coin-page">
      {/* HERO */}
      <div className="relative overflow-hidden border border-[#FCD535]/40 bg-gradient-to-br from-[#181A20] via-[#1E2026] to-[#0B0E11] p-8 sm:p-12 mb-8">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#FCD535] to-transparent" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FCD535]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase font-mono tracking-widest text-[#FCD535] mb-3">
            <Rocket className="w-3 h-3" /> For Projects & Founders
          </div>
          <h1 className="font-display text-white text-4xl sm:text-6xl font-bold tracking-tighter leading-none">
            List your token<br />on <span className="text-[#FCD535]">BITGONODE</span>
          </h1>
          <p className="text-[#B7BDC6] text-base sm:text-lg mt-6 leading-relaxed">
            Reach millions of active traders with a spot listing or ignite your token via our Launchpad. Fair-launch tokenomics, transparent process, dedicated growth support.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <a href="#apply" className="inline-block">
              <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-12 px-8 text-base" data-testid="hero-apply-cta">
                Start your application <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            {user && (
              <Link to="/my-listings" className="text-[#FCD535] hover:text-[#F0B90B] text-sm inline-flex items-center gap-1.5" data-testid="hero-my-apps">
                View my applications <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {[
          { icon: TrendingUp, title: 'Instant liquidity', body: "Access BITGONODE's active spot orderbook the day you list — thousands of concurrent traders every hour." },
          { icon: Rocket, title: 'Launchpad boost', body: 'Fair-launch your token with staged tokenomics, commitment tiers, and built-in marketing.' },
          { icon: ShieldCheck, title: 'Compliance first', body: 'Full KYC/AML review, contract audit, and legal check before every listing.' },
        ].map(b => (
          <div key={b.title} className="border border-[#2B3139] bg-[#181A20] p-5">
            <b.icon className="w-5 h-5 text-[#FCD535] mb-3" />
            <div className="text-white font-semibold text-sm">{b.title}</div>
            <div className="text-[#848E9C] text-xs mt-1.5 leading-relaxed">{b.body}</div>
          </div>
        ))}
      </div>

      {/* Fees + terms (admin editable) */}
      {info && (
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <div className="border border-[#2B3139] bg-[#181A20] p-5" data-testid="listing-fees-info">
            <div className="flex items-center gap-2 text-[#FCD535] uppercase text-[10px] tracking-widest mb-2">
              <Coins className="w-3 h-3" /> Listing Fees
            </div>
            <div className="text-[#B7BDC6] text-sm leading-relaxed whitespace-pre-line">{info.fee_details_text}</div>
            <div className="text-[#848E9C] text-xs mt-3 flex items-center gap-1.5">
              <Mail className="w-3 h-3" /> Questions? <a href={`mailto:${info.contact_email}`} className="text-[#FCD535] hover:underline">{info.contact_email}</a>
            </div>
          </div>
          <div className="border border-[#2B3139] bg-[#181A20] p-5">
            <div className="flex items-center gap-2 text-[#FCD535] uppercase text-[10px] tracking-widest mb-2">
              <ShieldCheck className="w-3 h-3" /> Review Terms
            </div>
            <div className="text-[#B7BDC6] text-sm leading-relaxed whitespace-pre-line">{info.terms_text}</div>
          </div>
        </div>
      )}

      {/* Application form or success */}
      <div id="apply" className="border border-[#2B3139] bg-[#181A20] p-6 sm:p-8">
        {submitted ? (
          <div className="text-center py-8" data-testid="listing-submitted-success">
            <CheckCircle2 className="w-14 h-14 text-[#0ECB81] mx-auto mb-4" />
            <h2 className="font-display text-white text-3xl font-bold tracking-tight">Application submitted</h2>
            <p className="text-[#848E9C] text-sm mt-2 max-w-lg mx-auto">
              We've received your listing application for <span className="text-[#FCD535] font-mono">{submitted.token_symbol}</span>.
              Reference: <span className="font-mono text-white">{submitted.application_id}</span>
            </p>
            <p className="text-[#848E9C] text-sm mt-4 max-w-lg mx-auto">
              Our team will review your application and reach out via email at{' '}
              <span className="text-white font-mono">{submitted.contact_email}</span> with fee details and next steps.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Link to="/my-listings">
                <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="submitted-view-apps">
                  View my applications
                </Button>
              </Link>
              <Button
                onClick={() => { setSubmitted(null); setForm({ ...emptyForm, contact_email: user?.email || '' }); }}
                className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm"
                data-testid="submitted-new-app"
              >
                Submit another
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="font-display text-white text-2xl sm:text-3xl font-bold tracking-tight">Submit your application</h2>
            <p className="text-[#848E9C] text-sm mt-1">
              All fields with <span className="text-[#F6465D]">*</span> are required. Applications are reviewed within 5–10 business days.
            </p>

            {!user && (
              <div className="mt-5 border-l-2 border-[#FCD535] bg-[#FCD535]/5 px-4 py-3 text-sm text-[#B7BDC6] flex items-center justify-between flex-wrap gap-2">
                <span>You'll need a BITGONODE account to submit. It only takes 30 seconds.</span>
                <div className="flex gap-2">
                  <Link to="/login"><Button size="sm" className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm h-8 text-xs">Log In</Button></Link>
                  <Link to="/signup"><Button size="sm" className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm h-8 text-xs font-semibold">Sign Up</Button></Link>
                </div>
              </div>
            )}

            <div className="mt-6 space-y-6">
              {/* Section: Basics */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FCD535] mb-3">1. Token Basics</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Symbol <span className="text-[#F6465D]">*</span></label>
                    <Input value={form.token_symbol} onChange={e => set('token_symbol', e.target.value.toUpperCase().slice(0, 12))}
                      placeholder="e.g. MYTOKEN" data-testid="listing-symbol"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Name <span className="text-[#F6465D]">*</span></label>
                    <Input value={form.token_name} onChange={e => set('token_name', e.target.value.slice(0, 80))}
                      placeholder="e.g. MyToken" data-testid="listing-name"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Category <span className="text-[#F6465D]">*</span></label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      {[['trade', 'Spot Trade', 'Get listed on the Spot orderbook'],
                        ['launchpad', 'Launchpad', 'Fair-launch your token'],
                        ['both', 'Both', 'Launch then get listed']].map(([v, label, desc]) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => set('category', v)}
                          className={`text-left px-3 py-3 border ${form.category === v ? 'bg-[#FCD535]/10 border-[#FCD535] text-white' : 'bg-[#0B0E11] border-[#2B3139] text-[#B7BDC6] hover:border-[#3A424C]'}`}
                          data-testid={`listing-category-${v}`}
                        >
                          <div className="text-xs uppercase font-mono tracking-wider">{label}</div>
                          <div className="text-[10px] text-[#848E9C] mt-1">{desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Logo URL</label>
                    <Input value={form.logo_url} onChange={e => set('logo_url', e.target.value)}
                      placeholder="https://…/logo.png" data-testid="listing-logo"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Description <span className="text-[#F6465D]">*</span> <span className="text-[#848E9C]">(min 40 chars)</span></label>
                    <textarea value={form.description} onChange={e => set('description', e.target.value.slice(0, 4000))}
                      rows={4} placeholder="Describe what your token does, target market, utility, and unique value proposition."
                      data-testid="listing-description"
                      className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-3 focus:outline-none focus:border-[#FCD535] resize-none" />
                    <div className="text-[10px] text-[#848E9C] mt-1 text-right">{form.description.length}/4000</div>
                  </div>
                </div>
              </div>

              {/* Section: Links */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FCD535] mb-3">2. Links & Community</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[['website', 'Website *', 'https://mytoken.io'],
                    ['whitepaper_url', 'Whitepaper URL', 'https://mytoken.io/whitepaper.pdf'],
                    ['twitter', 'Twitter', 'https://twitter.com/…'],
                    ['telegram', 'Telegram', 'https://t.me/…'],
                    ['discord', 'Discord', 'https://discord.gg/…'],
                    ['github', 'GitHub', 'https://github.com/…']].map(([k, label, ph]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">{label}</label>
                      <Input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
                        data-testid={`listing-${k}`}
                        className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Section: Tokenomics */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FCD535] mb-3">3. Tokenomics</div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Total Supply</label>
                    <Input type="number" value={form.total_supply} onChange={e => set('total_supply', e.target.value)}
                      placeholder="1000000" data-testid="listing-total-supply"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Circulating Supply</label>
                    <Input type="number" value={form.circulating_supply} onChange={e => set('circulating_supply', e.target.value)}
                      placeholder="100000" data-testid="listing-circulating-supply"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Initial Price (USD)</label>
                    <Input type="number" step="any" value={form.initial_price_usd} onChange={e => set('initial_price_usd', e.target.value)}
                      placeholder="0.10" data-testid="listing-initial-price"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]" />
                  </div>
                </div>
              </div>

              {/* Section: Pitch */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#FCD535] mb-3">4. Why BITGONODE?</div>
                <div className="grid gap-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Reason for listing <span className="text-[#F6465D]">*</span> <span className="text-[#848E9C]">(min 20 chars)</span></label>
                    <textarea value={form.reason} onChange={e => set('reason', e.target.value.slice(0, 2000))}
                      rows={3} placeholder="Tell us about your community, roadmap, and why BITGONODE is a good fit."
                      data-testid="listing-reason"
                      className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-3 focus:outline-none focus:border-[#FCD535] resize-none" />
                    <div className="text-[10px] text-[#848E9C] mt-1 text-right">{form.reason.length}/2000</div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Contact email</label>
                    <Input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)}
                      placeholder="founder@mytoken.io" data-testid="listing-contact"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]" />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  onClick={submit}
                  disabled={busy}
                  className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold h-12 text-base"
                  data-testid="listing-submit-btn"
                >
                  {busy ? 'Submitting…' : (user ? 'Submit application' : 'Log in and submit')}
                </Button>
                <p className="text-[10px] text-[#848E9C] text-center mt-3">
                  By submitting, you confirm all information provided is accurate. Fees are communicated separately by our team.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
